import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Zap, Globe, TrendingUp, Award, ChevronDown, Building2, BarChart3 } from 'lucide-react';
import ContactModal from '../components/ContactModal';
import StandardFooter from '../components/StandardFooter';

const HomePage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const [isContactOpen, setIsContactOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="offworld-container">
      {/* Animated Background Grid */}
      <div className="background-grid" style={{ transform: `translateY(${scrollY * 0.3}px)` }}></div>
      <div className="background-orbs"></div>

      {/* Space-themed Hero Background */}
      <div className="hero-space-bg">
        <div className="stars-layer stars-small"></div>
        <div className="stars-layer stars-medium"></div>
        <div className="stars-layer stars-large"></div>
        <div className="nebula nebula-1"></div>
        <div className="nebula nebula-2"></div>
        <div className="cosmic-grid"></div>
        
        {/* Orbital System */}
        <div className="orbit-system">
          <div className="orbit-path orbit-1">
            <div className="orbit-object"></div>
          </div>
          <div className="orbit-path orbit-2">
            <div className="orbit-object"></div>
          </div>
          <div className="orbit-path orbit-3">
            <div className="orbit-object"></div>
          </div>
        </div>

        {/* Floating Geometric Elements */}
        <div className="floating-geo geo-1"></div>
        <div className="floating-geo geo-2"></div>
        <div className="floating-geo geo-3"></div>
        <div className="floating-geo geo-4"></div>
      </div>

      {/* Navigation Header */}
      <header className="fixed-header">
        <div className="header-content">
          <div className="logo-container">
            <img src="/logo.png" alt="Offworld Ventures" className="logo" />
            <span className="logo-text-nav">OFFWORLD VENTURES</span>
          </div>
          <nav className="main-nav">
            <button onClick={() => scrollToSection('mission')} className="nav-link">Mission</button>
            <button onClick={() => scrollToSection('services')} className="nav-link">Services</button>
            <button onClick={() => scrollToSection('focus')} className="nav-link">How We Build</button>
            <Link to="/portfolio" className="nav-link">Portfolio</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="geometric-shape shape-1"></div>
          <div className="geometric-shape shape-2"></div>
          <div className="geometric-shape shape-3"></div>
          
          <h1 className="hero-title">
            <span className="title-line">WE BUILD</span>
            <span className="title-line gold-text">WHAT&apos;S NEXT</span>
          </h1>
          <p className="hero-subtitle">
            We build for the audacious. The ones who refuse to wait for the future—so we engineer it for them. AI-powered apps, tools, and platforms. From concept to launch in days, not months.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-value">BOLD</div>
              <div className="stat-label">Vision</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-value">RADICAL</div>
              <div className="stat-label">Innovation</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-value">LIMITLESS</div>
              <div className="stat-label">Impact</div>
            </div>
          </div>
          <div className="hero-stats hero-stats-proof">
            <div className="stat-item">
              <div className="stat-value">7+</div>
              <div className="stat-label">Products Live</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-value">AI-FIRST</div>
              <div className="stat-label">Every Build</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-value">DAYS</div>
              <div className="stat-label">Not Months</div>
            </div>
          </div>
          <button onClick={() => scrollToSection('mission')} className="scroll-indicator">
            <ChevronDown size={32} />
          </button>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="mission-section" data-animate>
        <div className={`content-wrapper ${isVisible['mission'] ? 'animate-in' : ''}`}>
          <div className="section-grid">
            <div className="grid-left">
              <div className="floating-badge">
                <Rocket size={40} />
              </div>
              <h2 className="section-title">Your Vision,<br /><span className="gold-text">Built and Shipped.</span></h2>
              <p className="section-description">
                Offworld Ventures exists at the intersection of ambition and reality. We&apos;re an AI development studio that partners with the bold—building apps, tools, and platforms for businesses who refuse to accept the status quo. And when we see a problem no one else is solving, we build the solution ourselves.
              </p>
              <p className="section-description">
                We are everywhere and nowhere. A borderless team of engineers, designers, and strategists united by one mission: build what&apos;s next, ship it fast, make it matter.
              </p>
            </div>
            <div className="grid-right">
              <div className="principle-card">
                <Zap className="principle-icon" size={32} />
                <h3 className="principle-title">Build for 10x</h3>
                <p className="principle-text">We don&apos;t do incremental. Every product we build—for clients or ourselves—is designed to be 10x better than what exists.</p>
              </div>
              <div className="principle-card">
                <Globe className="principle-icon" size={32} />
                <h3 className="principle-title">Ship Everywhere</h3>
                <p className="principle-text">Borderless products for a borderless world. We build technology that works across industries, markets, and continents.</p>
              </div>
              <div className="principle-card">
                <TrendingUp className="principle-icon" size={32} />
                <h3 className="principle-title">Own the Outcome</h3>
                <p className="principle-text">We don&apos;t build and walk away. Every product we launch gets continuous iteration, long-term support, and relentless evolution.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Build Section */}
      <section id="services" className="services-section" data-animate>
        <div className={`content-wrapper ${isVisible['services'] ? 'animate-in' : ''}`}>
          <h2 className="section-title-center">What We <span className="gold-text">Build</span></h2>
          <p className="section-subtitle-center">
            For our clients and for ourselves—every product is AI-powered, built in-house, and shipped fast.
          </p>

          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">
                <Globe size={32} />
              </div>
              <h3 className="service-title">Web Applications</h3>
              <p className="service-text">
                Full-stack web apps with AI baked in. Dashboards, marketplaces, custom ecommerce, internal tools—deployed, monitored, and continuously improved.
              </p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <Zap size={32} />
              </div>
              <h3 className="service-title">Mobile Apps</h3>
              <p className="service-text">
                Cross-platform mobile applications for consumers and businesses. Native performance, AI-powered features, shipped to both stores.
              </p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <Award size={32} />
              </div>
              <h3 className="service-title">AI Tools & Agents</h3>
              <p className="service-text">
                Custom AI agents, chatbots, automation pipelines, and intelligent workflows. We build AI that ships as product—not demos.
              </p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <TrendingUp size={32} />
              </div>
              <h3 className="service-title">SaaS Platforms</h3>
              <p className="service-text">
                End-to-end SaaS products from architecture and billing to launch and growth. We&apos;ve done it 7+ times for ourselves—we&apos;ll do it for you.
              </p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <Building2 size={32} />
              </div>
              <h3 className="service-title">Internal Tools</h3>
              <p className="service-text">
                Intranets, workforce management, CRM systems, and custom dashboards. The tools your team actually needs—built fast, integrated deep.
              </p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <BarChart3 size={32} />
              </div>
              <h3 className="service-title">Data & Analytics</h3>
              <p className="service-text">
                Real-time dashboards, BI platforms, and data pipelines that turn raw information into decisions. AI-powered insights your competitors don&apos;t have.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Build Section */}
      <section id="focus" className="focus-section" data-animate>
        <div className={`content-wrapper ${isVisible['focus'] ? 'animate-in' : ''}`}>
          <h2 className="section-title-center">How We <span className="gold-text">Build</span></h2>
          <p className="section-subtitle-center">
            Whether it&apos;s your product or ours, here&apos;s how every build works at Offworld.
          </p>
          
          <div className="how-invest-grid">
            <div className="how-invest-card">
              <div className="how-invest-number">01</div>
              <h3 className="how-invest-title">Obsession</h3>
              <p className="how-invest-description">
                Every project starts the same way: we dig into the problem until we understand it cold. Your industry, your users, your constraints. We don&apos;t write a single line of code until we&apos;re obsessed with the problem.
              </p>
              <div className="how-invest-list">
                <div className="how-invest-item">• Deep problem discovery & research</div>
                <div className="how-invest-item">• Industry pain point analysis</div>
                <div className="how-invest-item">• User-centric solution design</div>
                <div className="how-invest-item">• Relentless iteration until solved</div>
              </div>
              <div className="focus-line"></div>
            </div>

            <div className="how-invest-card">
              <div className="how-invest-number">02</div>
              <h3 className="how-invest-title">Velocity</h3>
              <p className="how-invest-description">
                Ideas are worthless without execution. You get working software fast—not wireframes, not proposals, not pitch decks. Software. We leverage AI and modern development to move from concept to live product in days, not months.
              </p>
              <div className="how-invest-list">
                <div className="how-invest-item">• AI-accelerated development</div>
                <div className="how-invest-item">• Rapid prototyping & validation</div>
                <div className="how-invest-item">• Continuous deployment pipelines</div>
                <div className="how-invest-item">• Ship fast, learn faster</div>
              </div>
              <div className="focus-line"></div>
            </div>

            <div className="how-invest-card">
              <div className="how-invest-number">03</div>
              <h3 className="how-invest-title">Impact</h3>
              <p className="how-invest-description">
                We measure success by problems eliminated, not products shipped. Every tool we build, every platform we launch, exists to make something measurably better than we found it.
              </p>
              <div className="how-invest-list">
                <div className="how-invest-item">• Outcomes over outputs</div>
                <div className="how-invest-item">• Real-world problem elimination</div>
                <div className="how-invest-item">• Scalable, lasting solutions</div>
                <div className="how-invest-item">• Measurable transformation</div>
              </div>
              <div className="focus-line"></div>
            </div>
          </div>

          <div className="cta-section" style={{ marginTop: '80px' }}>
            <h3 className="cta-title">Have a problem worth obsessing over?</h3>
            <p className="cta-text">An app that needs building? A platform that doesn&apos;t exist yet? A tool that should be smarter? We&apos;re listening.</p>
            <button className="cta-contact-button" onClick={() => setIsContactOpen(true)}>
              Let&apos;s Build It
            </button>
          </div>
        </div>
      </section>

      {/* Our Philosophy Section */}
      <section id="philosophy" className="portfolio-section" data-animate>
        <div className={`content-wrapper ${isVisible['philosophy'] ? 'animate-in' : ''}`}>
          <div className="portfolio-content">
            <h2 className="section-title-center">Our <span className="gold-text">Philosophy</span></h2>
            <p className="portfolio-large-text">
              Whether we&apos;re building for a client or for ourselves, the approach never changes. We own the problem. We own the build. We own the outcome.
            </p>
            
            <div className="approach-grid">
              <div className="approach-item">
                <h3 className="approach-title">Full Ownership</h3>
                <p className="approach-text">
                  Whether it&apos;s your product or ours—no handoffs, no outsourcing. We own it end-to-end, from first line of code to final deployment and beyond.
                </p>
              </div>
              
              <div className="approach-item">
                <h3 className="approach-title">In-House Expertise</h3>
                <p className="approach-text">
                  Engineers, designers, and strategists—all under one roof. We don&apos;t assemble teams; we are the team.
                </p>
              </div>
              
              <div className="approach-item">
                <h3 className="approach-title">AI-First</h3>
                <p className="approach-text">
                  Every solution we build has AI at its core. Not as an afterthought, but as the foundation that makes the impossible possible.
                </p>
              </div>
              
              <div className="approach-item">
                <h3 className="approach-title">Built to Last</h3>
                <p className="approach-text">
                  We don&apos;t build and abandon. Every product gets continuous iteration, long-term support, and relentless evolution.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <StandardFooter onContactClick={() => setIsContactOpen(true)} />

      {/* Contact Modal */}
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </div>);

};

export default HomePage;
